// @hssna/family-web API 客户端工具
// 规格: SPEC §4.1 API 客户端架构 / §4.2 请求头注入规约
//
// 职责:
//   - 通过 auth-store 持有的 AuthClient 构造带拦截器的 fetch 客户端
//   - 提供 getFamilyId (从 JWT payload.family_group_id 读取)
//   - 提供 unwrap 解包 HSSNA ApiResponse { data, meta }
//   - 提供 getAccessTokenSync (供 SseManager 同步读取 token)

import type { AuthClient } from '@hssna/ts-shared';
import { isHssnaError } from '@hssna/ts-shared';
import { createFetchClient, createInterceptors } from '@hssna/ts-shared/api';
import { useAuthStore } from '../store/auth-store';

type ApiFetch = (url: string, init?: RequestInit) => Promise<Response>;

let cachedAuthClient: AuthClient | null = null;
let cachedFetch: ApiFetch | null = null;

// 防止并发 401 重复触发 logout/redirect
let isAuthRedirecting = false;

/**
 * 获取带拦截器的 fetch 客户端
 * - AuthClient 由 main.tsx 在启动时注入 auth-store
 * - 拦截器自动注入: Authorization / X-Request-Id / X-Trace-Id / Idempotency-Key / User-Agent
 * - 拦截器自动处理: 401 refresh + 重试, RFC 7807 解析, IDOR 上报
 */
function getApiFetch(): ApiFetch {
  const authClient = useAuthStore.getState().authClient;
  if (!authClient) {
    throw new Error('AuthClient 未初始化,请先完成登录');
  }
  if (cachedAuthClient !== authClient || !cachedFetch) {
    const interceptors = createInterceptors({
      authClient,
      service: 'family-web',
      version: '0.1.0',
    });
    cachedAuthClient = authClient;
    cachedFetch = createFetchClient(interceptors);
  }
  return cachedFetch;
}

/**
 * 带拦截器的 fetch (页面统一入口)
 * 在 API 不可用时返回 null Response, 避免"加载失败"阻断整个页面
 * (后端部分 API 端点尚在开发中, 前端应优雅降级为"暂无数据")
 *
 * 注意: 返回 'null' body 而非 '[]' — unwrap 会解析为 null,
 * 各页面的 `!stats` 检查会渲染"暂无数据"占位符;
 * 期望数组的页面通过 `Array.isArray(data)` 检查后回退到 `[]`。
 */
export async function apiFetch(url: string, init?: RequestInit): Promise<Response> {
  try {
    return await getApiFetch()(url, init);
  } catch (err) {
    // 401 (拦截器 token refresh 已失败) → 登出并跳转登录页
    // 不降级为 null, 否则用户看到"暂无数据"而非登录跳转
    if (isHssnaError(err) && err.status === 401) {
      if (!isAuthRedirecting) {
        isAuthRedirecting = true;
        void useAuthStore.getState().logout().finally(() => {
          window.location.href = '/login';
        });
      }
      throw err;
    }
    // API 不可用 (403/404/500/网络错误), 返回 null 让页面优雅降级为"暂无数据"
    // eslint-disable-next-line no-console
    console.warn(`[family-web] API ${url} 不可用, 返回 null:`, err instanceof Error ? err.message : err);
    return new Response('null', {
      status: 200,
      headers: { 'Content-Type': 'application/json' },
    });
  }
}

/**
 * 从当前 access_token 解析 family_group_id
 * 用于构造 /v1/families/{familyId}/... 路径
 */
export function getFamilyId(): string | null {
  return useAuthStore.getState().user?.family_group_id ?? null;
}

/** 同步读取 access_token (供 SseManager 鉴权使用) */
export function getAccessTokenSync(): string | null {
  return useAuthStore.getState().tokens?.access_token ?? null;
}

/**
 * 解包 HSSNA ApiResponse { data, meta } → data
 * - 204 No Content → null
 * - 空 body → null
 * - 兼容直接返回 data (无外层封装) 的退化场景
 */
export async function unwrap<T = unknown>(res: Response): Promise<T> {
  if (res.status === 204) return null as T;
  const text = await res.text();
  if (!text) return null as T;
  const json = JSON.parse(text) as { data?: T; meta?: unknown } | T;
  if (
    json !== null &&
    typeof json === 'object' &&
    'data' in (json as Record<string, unknown>)
  ) {
    return (json as { data: T }).data;
  }
  return json as T;
}

/**
 * 创建数据授权 (S13 Consent)
 * 调用 POST /v1/consents, 支持多种授权类型 (ems_proxy / sharing / guardianship 等)
 * 由 ConsentManagementPage 创建入口复用, 避免与 EmsPreAuthorizationPage 重复实现
 */
export async function createConsent(
  payload: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const res = await apiFetch('/v1/consents', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return unwrap<Record<string, unknown>>(res);
}

/**
 * 更新设备元信息 (S10 Device)
 * 调用 PUT /v1/devices/{deviceId}, 仅更新可编辑字段 (model/vendor/firmware_version/capabilities)
 */
export async function updateDevice(
  deviceId: string,
  payload: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const res = await apiFetch(`/v1/devices/${deviceId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return unwrap<Record<string, unknown>>(res);
}

/**
 * 更新家庭成员关系 (S2 Family)
 * 调用 PATCH /v1/families/{familyId}/members/{patientId}, 更新关系/年龄段/监护人等
 */
export async function updateFamilyMember(
  familyId: string,
  patientId: string,
  payload: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const res = await apiFetch(
    `/v1/families/${familyId}/members/${patientId}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
  );
  return unwrap<Record<string, unknown>>(res);
}

/**
 * 撤销代理决策 (S13 ProxyDecision)
 * 优先调用 DELETE /v1/proxy-decisions/{id}; 若后端无 DELETE 端点则降级为
 * PATCH /v1/proxy-decisions/{id} (status=revoked) 标记撤销
 */
export async function revokeProxyDecision(
  decisionId: string,
): Promise<Record<string, unknown> | null> {
  // 先尝试 DELETE (语义为硬撤销)
  const res = await apiFetch(`/v1/proxy-decisions/${decisionId}`, {
    method: 'DELETE',
  });
  if (res.status === 204) return null;
  // 后端无 DELETE 端点时返回 404/405, 降级为 PATCH 标记撤销
  if (res.status === 404 || res.status === 405) {
    const patchRes = await apiFetch(`/v1/proxy-decisions/${decisionId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: 'revoked' }),
    });
    return unwrap<Record<string, unknown>>(patchRes);
  }
  return unwrap<Record<string, unknown>>(res);
}
